import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { 
  BarChart3, 
  Clock, 
  TrendingUp, 
  Zap, 
  Shield, 
  Users,
  ArrowRight,
  CheckCircle,
  Target,
  DollarSign,
  Award,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Benefits = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [currentBenefitIndex, setCurrentBenefitIndex] = React.useState(0);
  const benefitEntries = Object.entries(t.benefits.items);

  const benefitIcons = {
    efficiency: <Clock className="w-10 h-10" style={{ color: '#115d7b' }} />,
    growth: <TrendingUp className="w-10 h-10" style={{ color: '#115d7b' }} />,
    data: <BarChart3 className="w-10 h-10" style={{ color: '#115d7b' }} />,
    speed: <Zap className="w-10 h-10" style={{ color: '#115d7b' }} />,
    security: <Shield className="w-10 h-10" style={{ color: '#115d7b' }} />,
    collaboration: <Users className="w-10 h-10" style={{ color: '#115d7b' }} />
  };

  const nextBenefit = () => {
    setCurrentBenefitIndex((prev) => (prev + 1) % benefitEntries.length);
  };

  const prevBenefit = () => {
    setCurrentBenefitIndex((prev) => (prev - 1 + benefitEntries.length) % benefitEntries.length);
  };

  // Auto-advance carousel every 5 seconds on mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (window.innerWidth < 768) {
        nextBenefit();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  const impactMetrics = [
    {
      icon: <Target className="w-8 h-8" style={{ color: '#ff8c00' }} />,
      value: '500+',
      label: language === 'en' ? 'Companies Transformed' : 'Empresas Transformadas',
      description: language === 'en' ? 'Across Mexico and Latin America' : 'En México y Latinoamérica'
    },
    {
      icon: <DollarSign className="w-8 h-8" style={{ color: '#10b981' }} />,
      value: '$50M+',
      label: language === 'en' ? 'Cost Savings Generated' : 'Ahorros Generados',
      description: language === 'en' ? 'For our clients annually' : 'Para nuestros clientes anualmente'
    },
    {
      icon: <Award className="w-8 h-8" style={{ color: '#8b5cf6' }} />,
      value: '98%',
      label: language === 'en' ? 'Client Satisfaction' : 'Satisfacción del Cliente',
      description: language === 'en' ? 'Would recommend us' : 'Nos recomendarían'
    },
    {
      icon: <TrendingUp className="w-8 h-8" style={{ color: '#ef4444' }} />,
      value: '6 months',
      label: language === 'en' ? 'Average ROI Time' : 'Tiempo Promedio de ROI',
      description: language === 'en' ? 'To see full return' : 'Para ver retorno completo'
    }
  ];

  return (
    <section id="benefits" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
            <Award className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Proven Results That Matter' : 'Resultados Comprobados Que Importan'}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t.benefits.title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {t.benefits.subtitle}
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden mb-20">
          <div className="relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentBenefitIndex * 100}%)` }}
              >
                {benefitEntries.map(([key, benefit]) => (
                  <div key={key} className="w-full flex-shrink-0 px-4">
                    <div className="group bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-transparent opacity-50"></div>
                      
                      <div className="relative z-10 text-center">
                        <div className="mb-6 flex justify-center">
                          {benefitIcons[key as keyof typeof benefitIcons]}
                        </div>
                        <h3 className="text-2xl font-bold mb-4 text-gray-900">
                          {benefit.title}
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {benefit.description}
                        </p>
                        
                        {benefit.metrics && (
                          <div className="mt-8 pt-6 border-t border-gray-200">
                            <div className="text-center">
                              <span className="text-4xl font-bold block" style={{ color: '#115d7b' }}>
                                {benefit.metrics.value}
                              </span>
                              <div className="text-gray-500 text-sm mt-1">
                                {benefit.metrics.label}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: '#115d7b' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button 
              onClick={prevBenefit} 
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              style={{ color: '#115d7b' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <button 
              onClick={nextBenefit} 
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
              style={{ color: '#115d7b' }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {benefitEntries.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBenefitIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    currentBenefitIndex === index ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  style={currentBenefitIndex === index ? { backgroundColor: '#115d7b' } : {}}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 mb-20">
          {benefitEntries.map(([key, benefit]) => (
            <div 
              key={key}
              className="group bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-500 relative overflow-hidden transform hover:-translate-y-2"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {benefitIcons[key as keyof typeof benefitIcons]}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-blue-900 transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {benefit.description}
                </p>
                
                {benefit.metrics && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-4xl font-bold" style={{ color: '#115d7b' }}>
                          {benefit.metrics.value}
                        </span>
                        <div className="text-gray-500 text-sm mt-1">
                          {benefit.metrics.label}
                        </div>
                      </div>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#e0f2fe' }}>
                        <TrendingUp className="w-6 h-6" style={{ color: '#115d7b' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500" style={{ backgroundColor: '#115d7b' }}></div>
            </div>
          ))}
        </div>

        {/* Success Case Study */}
        <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(to right, #115d7b, #0f5269)' }}>
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-8 md:p-12 text-white">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: 'rgba(255, 140, 0, 0.2)', color: '#ff8c00' }}>
                <Award className="w-4 h-4 mr-2" />
                {language === 'en' ? 'Success Story' : 'Historia de Éxito'}
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                {t.benefits.caseStudy.title}
              </h3>
              <p className="text-xl text-white/90 mb-8">
                {t.benefits.caseStudy.description}
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                {t.benefits.caseStudy.results.map((result, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">
                      {index === 0 ? '45%' : index === 1 ? '80%' : index === 2 ? '300%' : '6 meses'}
                    </div>
                    <div className="text-white/80 text-sm">{result}</div>
                  </div>
                ))}
              </div>
              <a 
                href="/success-cases"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-bold shadow-lg"
              >
                {t.cta.caseStudy}
                <ArrowRight className="ml-3 h-5 w-5" />
              </a>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                alt={language === 'en'
                  ? 'Corporate team using LAIMU ERP system - Manufacturing business success in Mexico with integrated management software'
                  : 'Equipo corporativo usando sistema ERP LAIMU - Éxito empresarial manufacturero en México con software de gestión integrado'}
                className="w-full h-full object-cover min-h-[400px]"
                loading="lazy"
                width="1260"
                height="750"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to left, transparent, rgba(17, 93, 123, 0.3))' }} aria-hidden="true"></div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-2xl border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'en' 
                ? 'Ready to Transform Your Business?' 
                : '¿Listo para Transformar Tu Negocio?'
              }
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Join hundreds of companies that have already revolutionized their operations with LAIMU Trantor.'
                : 'Únete a cientos de empresas que ya han revolucionado sus operaciones con LAIMU Trantor.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/demo"
                className="px-8 py-4 text-white rounded-lg transition-colors font-bold text-lg shadow-lg"
                style={{ backgroundColor: '#115d7b' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
              >
                {language === 'en' ? 'Get Free Demo' : 'Obtener Demo Gratis'}
              </a>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 border-2 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg"
                style={{ borderColor: '#115d7b' }}
              >
                {language === 'en' ? 'Talk to Expert' : 'Hablar con Experto'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;